/**
 * One patient's record as a FHIR file — DECISIONS.md #52.
 *
 * Mirrors `api/src/Continuum.Api/Export/ExportEndpoints.cs`. The server gates it like a write and
 * commits the `export` row — with the file's SHA-256 (DECISIONS.md #53) — before it sends a byte, so
 * by the time this has bytes the access is already in the log, and a refusal is in it too.
 *
 * **Fetched and handed to the browser as a download**, rather than a plain link, so a refusal or a
 * missing patient becomes a sentence on the screen instead of a file named `.json` holding an error.
 * The file name is the server's — dated, and with no identifier in it.
 */

import { unauthenticated } from '../auth/sessionExpiry'

export type ExportFailure = 'refused' | 'not-found' | 'unavailable'

export class ExportError extends Error {
  readonly failure: ExportFailure

  constructor(failure: ExportFailure) {
    super(`Export failed: ${failure}`)
    this.name = 'ExportError'
    this.failure = failure
  }
}

/** The file name from `Content-Disposition`, or a dated fallback that names nobody. */
function fileName(response: Response): string {
  const header = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(header)
  return match?.[1] ?? 'continuum-export.json'
}

/** Fetches the export. Separate from saving it, so the fetch is testable without a DOM. */
export async function fetchFhirExport(
  patientId: string,
  signal: AbortSignal,
): Promise<{ readonly blob: Blob; readonly name: string }> {
  let response: Response

  try {
    response = await fetch(`/api/patients/${encodeURIComponent(patientId)}/export/fhir`, {
      signal,
      headers: { Accept: 'application/fhir+json' },
    })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new ExportError('unavailable')
  }

  unauthenticated(response)

  if (response.status === 403) {
    throw new ExportError('refused')
  }
  if (response.status === 404) {
    throw new ExportError('not-found')
  }
  if (!response.ok) {
    throw new ExportError('unavailable')
  }

  try {
    return { blob: await response.blob(), name: fileName(response) }
  } catch {
    throw new ExportError('unavailable')
  }
}

/** Hands a fetched file to the browser as a download. */
export function saveFile(file: { readonly blob: Blob; readonly name: string }): void {
  const url = URL.createObjectURL(file.blob)
  const link = document.createElement('a')
  link.href = url
  link.download = file.name
  link.click()
  URL.revokeObjectURL(url)
}
